/**
 * @author Simeon Artamonov <simeon@cross-works.co.uk>
 * @company Cross Works Ltd
 */
import { ref, computed } from 'vue'

type Side = 'left' | 'right'

export function useDrag() {
  const dragSide = ref<Side | null>(null)
  const startX = ref(0)
  const startPosition = ref(0) // Track the actual starting position
  const isDragging = computed(() => dragSide.value !== null)

  // Pointer event detection
  const hasPointer = 'PointerEvent' in window
  const pointerMoveEvent = hasPointer ? 'pointermove' : 'touchmove'
  const pointerEndEvent = hasPointer ? 'pointerup' : 'touchend'
  const pointerCancelEvent = hasPointer ? 'pointercancel' : 'touchcancel'

  const getClientX = (e: MouseEvent | TouchEvent | PointerEvent) =>
    'clientX' in e ? e.clientX : (e as TouchEvent).touches?.[0].clientX ?? 0

  function startDrag(
    e: PointerEvent | MouseEvent | TouchEvent,
    side: Side,
    isOpen: boolean,
    onComplete: (shouldOpen: boolean) => void,
    isPreview: boolean = false
  ) {
    // Prevent multiple drags
    if (dragSide.value) return
    
    dragSide.value = side
    startX.value = getClientX(e)

    const panel = document.querySelector<HTMLElement>(`.side-panel.${side}`)
    if (!panel) {
      console.warn('Panel not found for side:', side)
      dragSide.value = null
      return
    }

    panel.classList.add('dragging')
    
    const computedStyle = getComputedStyle(panel)
    const transform = computedStyle.transform
    let currentPosition = 0
    
    if (transform && transform !== 'none') {
      const matrix = new DOMMatrix(transform)
      const panelWidth = panel.offsetWidth || window.innerWidth * 0.84
      currentPosition = (matrix.m41 / panelWidth) * 100
    } else {
      if (isOpen) {
        currentPosition = 0 // Fully open
      } else if (isPreview) {
        currentPosition = side === 'left' ? -80 : 80 // Preview position
      } else {
        currentPosition = side === 'left' ? -100 : 100 // Fully closed
      }
    }
    
  
    panel.style.transform = `translateX(${currentPosition}%)`
    startPosition.value = currentPosition
    
    panel.dataset.initialState = isOpen ? 'open' : 'closed'
    
    const move = (ev: any) => handleMove(ev, panel, side)
    const end = (ev: any) => handleEnd(ev, panel, side, isOpen, onComplete, move, end)

    // Use passive: false to ensure preventDefault works
    document.addEventListener(pointerMoveEvent, move, { passive: false })
    document.addEventListener(pointerEndEvent, end, { passive: false })
    document.addEventListener(pointerCancelEvent, end, { passive: false })

    e.preventDefault?.()
  }

  function handleMove(
    e: PointerEvent | MouseEvent | TouchEvent,
    panel: HTMLElement,
    side: Side
  ) {
    if (!dragSide.value || !panel) return

    const currentX = getClientX(e)
    const deltaX = currentX - startX.value
    
    // More precise percentage calculation
    const panelWidth = panel.offsetWidth || window.innerWidth * 0.84 // Fallback width
    const percent = (deltaX / panelWidth) * 100
    
    // Calculate new position from the actual starting position
    let pos = startPosition.value + percent

    document.body.classList.add('panel-dragging')
    
    // Clamp the position to valid ranges
    if (side === 'left') {
      pos = Math.min(0, Math.max(-100, pos))

      
      
      if (pos > -20) {
        document.body.classList.add('panel-near-open')
      } else {
        document.body.classList.remove('panel-near-open')
      }
    } else {
      pos = Math.max(0, Math.min(100, pos))
      
      if (pos < 20) {
        document.body.classList.add('panel-near-open')
      } else {
        document.body.classList.remove('panel-near-open')
      }
    }

    // Apply the transform
    panel.style.transform = `translateX(${pos}%)`
    e.preventDefault?.()
  }

  function handleEnd(
    _e: PointerEvent | MouseEvent | TouchEvent,
    panel: HTMLElement,
    side: Side,
    isInitiallyOpen: boolean,
    onComplete: (shouldOpen: boolean) => void,
    move: EventListenerOrEventListenerObject,
    end: EventListenerOrEventListenerObject
  ) {
    if (!dragSide.value || !panel) return

    // Clean up immediately to prevent multiple calls
    dragSide.value = null

    document.body.classList.remove('panel-dragging')

    // Get final position
    const match = panel.style.transform.match(/translateX\(([-\d.]+)%\)/)
    const pos = match ? parseFloat(match[1]) : startPosition.value
    

    let shouldOpen = false
    if (side === 'left') {
      if (isInitiallyOpen) {
        // Panel started open: close if dragged more than 30% closed
        shouldOpen = pos > -40
      } else {
        // Panel started closed: open if dragged more than 30% open
        shouldOpen = pos > -60
      }
    } else {
      if (isInitiallyOpen) {
        // Panel started open: close if dragged more than 30% closed
        shouldOpen = pos < 40
      } else {
        // Panel started closed: open if dragged more than 30% open
        shouldOpen = pos < 60
      }
    }

    panel.classList.remove('dragging')
    
    // If the state isn't changing, reset to original position
    if (shouldOpen === isInitiallyOpen) {
      // Reset to original position - no midway positions allowed
      const resetPosition = isInitiallyOpen ? 0 : (side === 'left' ? -100 : 100)
      panel.style.transform = `translateX(${resetPosition}%)`
      panel.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
      
      // Clear transform after animation
      setTimeout(() => {
        panel.style.transform = ''
        panel.style.transition = ''
      }, 300)
    }
    // If state is changing, keep current position and let GSAP take over

    // Clean up
    delete panel.dataset.initialState
    document.removeEventListener(pointerMoveEvent, move)
    document.removeEventListener(pointerEndEvent, end)
    document.removeEventListener(pointerCancelEvent, end)

    
    document.body.classList.remove('panel-near-open')
    
    // Reset start position
    startPosition.value = 0
    
    // Call completion callback (only if state is changing)
    try {
      if (shouldOpen !== isInitiallyOpen) {
        onComplete(shouldOpen)
      }
    } catch (error) {
      console.error('Error in drag completion callback:', error)
    }
  }

  return {
    isDragging,
    dragSide,
    startDrag
  }
} 